import React from "react";

const LAST_UPDATED = "June 5, 2026";

// Full Privacy Policy & Terms of Use content. Reused by the dedicated page
// and the in-app consent dialog. Not a substitute for legal advice.
export const PolicyContent = ({ compact = false }) => {
  const h = (t) => (
    <h3 className={`font-display font-bold text-slate-100 ${compact ? "text-base mt-5" : "text-lg mt-7"}`}>{t}</h3>
  );
  const p = (children) => (
    <p className={`text-slate-300 leading-relaxed ${compact ? "text-sm mt-2" : "mt-3"}`}>{children}</p>
  );
  const li = (children, key) => (
    <li key={key} className={`text-slate-300 leading-relaxed ${compact ? "text-sm" : ""}`}>{children}</li>
  );

  return (
    <div data-testid="policy-content">
      <p className="text-xs uppercase tracking-wide text-slate-500">Last updated: {LAST_UPDATED}</p>

      {p(
        <>
          Welcome to <b>SwotKit</b> ("SwotKit", "we", "us", or "our"). By creating a business plan,
          generating a report, downloading a PDF, or otherwise using the SwotKit service (the
          "Service"), you agree to this Privacy Policy &amp; Terms of Use (this "Agreement"). If you do
          not agree, please do not use the Service.
        </>
      )}

      {h("1. What SwotKit Is — and Is Not")}
      {p(
        <>
          SwotKit is an <b>idea-development and brainstorming tool</b>. It helps you explore and
          structure business ideas through AI-assisted SWOT analyses, competitor overviews, pricing
          estimates, action plans, brand concepts and related outputs (collectively, the "Reports").
        </>
      )}
      {p(
        <>
          The Reports are <b>starting points for your own thinking only</b>. They are <b>not</b>{" "}
          professional advice of any kind — including legal, financial, investment, accounting, tax,
          regulatory or business consulting advice — and must not be relied upon as such. SwotKit does
          not evaluate, validate, endorse, or guarantee the viability, profitability, legality, or
          success of any idea, product, or business.
        </>
      )}

      {h("2. No Guarantee of Results")}
      {p(
        <>
          Business outcomes depend on countless factors outside our control, including your own
          decisions, execution, market conditions, competition and timing. <b>We make no promises or
          guarantees</b> about any result, revenue, profit, funding, or other outcome you may obtain.
          Any decision you make based on a Report is made <b>entirely at your own risk and
          discretion</b>.
        </>
      )}

      {h("3. AI-Generated Content")}
      {p(
        <>
          Reports are produced in part by third-party artificial-intelligence models. AI output can be
          <b> incomplete, outdated, biased, or factually incorrect</b> ("hallucinations"), and may not
          reflect current laws, prices, competitors or market data. You are solely responsible for
          independently verifying any information before acting on it.
        </>
      )}

      {h("4. Your Responsibilities")}
      <ul className={`list-disc pl-5 space-y-1.5 ${compact ? "mt-2" : "mt-3"}`}>
        {li("You will independently verify facts, figures and assumptions in any Report.", "r1")}
        {li("You are responsible for your own business decisions and their consequences.", "r2")}
        {li("You will comply with all applicable laws and regulations in your use of the Service.", "r3")}
        {li("You will not submit unlawful, infringing, or confidential third-party content.", "r4")}
      </ul>

      {h("5. Disclaimer of Warranties")}
      {p(
        <>
          The Service and all Reports are provided <b>"AS IS" and "AS AVAILABLE," without warranties of
          any kind</b>, whether express or implied, including (without limitation) implied warranties of
          merchantability, fitness for a particular purpose, accuracy, and non-infringement. We do not
          warrant that the Service will be uninterrupted, error-free, or secure.
        </>
      )}

      {h("6. Limitation of Liability")}
      {p(
        <>
          To the maximum extent permitted by law, in no event shall SwotKit, its owners, operators,
          employees, affiliates or suppliers be liable for <b>any indirect, incidental, special,
          consequential, exemplary or punitive damages</b>, or for <b>any loss of profits, revenue,
          data, goodwill, business opportunities, or failed or unsuccessful projects</b>, arising out of
          or relating to your use of (or inability to use) the Service or any Report — even if we have
          been advised of the possibility of such damages. Our total aggregate liability for any claim
          relating to the Service shall not exceed the greater of (a) the total amount you paid us for
          the Service in the twelve (12) months preceding the claim, or (b) USD $50.
        </>
      )}

      {h("7. Indemnification")}
      {p(
        <>
          You agree to indemnify, defend and hold harmless SwotKit and its owners and operators from and
          against any claims, liabilities, damages, losses and expenses (including reasonable legal
          fees) arising from your use of the Service, your Reports, your business decisions, or your
          violation of this Agreement or any law or third-party right.
        </>
      )}

      {h("8. Privacy & Data Handling")}
      {p("To provide the Service, we collect and process a limited amount of information:")}
      <ul className={`list-disc pl-5 space-y-1.5 ${compact ? "mt-2" : "mt-3"}`}>
        {li(<><b>Account data</b> from Google Sign-In (your name, email address and profile picture).</>, "d1")}
        {li(<><b>Content you create</b> (business names, descriptions, and the Reports generated for you).</>, "d2")}
        {li(<><b>Payment status</b> for premium access. Payments are processed by <b>PayPal</b>; we never see or store your full card details.</>, "d3")}
      </ul>
      {p(
        <>
          To generate Reports, the content you provide is sent to third-party AI providers (such as
          OpenAI, Anthropic and Google) for processing. We use your data to operate and improve the
          Service. <b>We do not sell your personal data.</b> We retain your data for as long as your
          account is active; you may request deletion of your account and associated plans at any time
          by contacting us. We apply reasonable technical and organizational measures to protect your
          data, but no method of transmission or storage is 100% secure.
        </>
      )}

      {h("9. Intellectual Property")}
      {p(
        <>
          You retain ownership of the inputs you provide. Subject to this Agreement, the Reports
          generated for you are yours to use for your own business purposes. The SwotKit name, software,
          and design remain our property. AI-generated output may not be unique to you and similar
          output may be generated for others.
        </>
      )}

      {h("10. Third-Party Services")}
      {p(
        <>
          The Service relies on third parties (e.g., Google, PayPal, and AI providers) whose own terms
          and privacy policies apply. We are not responsible for the acts, omissions, availability or
          content of any third-party service.
        </>
      )}

      {h("11. Changes to This Agreement")}
      {p(
        <>
          We may update this Agreement from time to time. Material changes will be reflected by the
          "Last updated" date above. Your continued use of the Service after changes become effective
          constitutes acceptance of the revised Agreement.
        </>
      )}

      {h("12. Governing Law")}
      {p(
        <>
          This Agreement is governed by the laws of the jurisdiction in which SwotKit's operator is
          established, without regard to conflict-of-law principles. If any provision is held
          unenforceable, the remaining provisions remain in full force and effect.
        </>
      )}

      {h("13. Contact")}
      {p(
        <>
          Questions about this Agreement or your data? Contact us at{" "}
          <a href="mailto:support@swotkit.com" className="text-teal-300 underline">support@swotkit.com</a>.
        </>
      )}

      {p(
        <span className="text-slate-400 text-sm italic">
          By using SwotKit you acknowledge that you have read, understood and agree to this Agreement,
          and that SwotKit is a brainstorming aid for which no responsibility is accepted for the
          success or failure of any project based on its Reports.
        </span>
      )}
    </div>
  );
};

export default PolicyContent;
